import React, { useState, useRef, useEffect } from "react";
import "./ChatPopup.css";
import instance from "../../services/baseService";
import chatIcon from "../../images/chatIcon.svg";
import chatbox from "../../images/chatbox.png";
import { useDispatch, useSelector } from "react-redux";
import { clearFcmChat, clearNotification } from '../../slices/notificationReducer';

const ChatPopup = ({ client_id, chat_type }) => {
  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = useState(false);
  const [closing, setClosing] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isSending, setIsSending] = useState(false); // Add state to prevent double clicks
  const { isNotificationRoute, notification, fcmNotification, newNotificationFound, fcmChatNotificationReceived, fcmChatParams } = useSelector((state) => state.notification);

  const chatBoxRef = useRef(null);

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    try {
      const response = await instance.post(`/api/v1/chat/list`, {
        client_id: client_id,
        message: null,
        chat_type: chat_type,
        page: 1,
        perpage: 25,
      });

      setMessages(response?.data?.data?.list);
    } catch (error) {
      console.error("Error fetching messages:", error);
    }
  };

  useEffect(() => {
    if (chatBoxRef.current) {
      chatBoxRef.current.scrollTop = chatBoxRef.current.scrollHeight;
      //   chatBoxRef.current.scrollTop = 0;
    }
  }, [messages, isOpen]);

  const handleSend = async () => {
    // Prevent sending if input is empty, only whitespace, or already sending
    if (!input.trim() || isSending) {
      return;
    }

    setIsSending(true); // Set sending state to prevent double clicks

    try {
      const response = await instance.post(`/api/v1/chat/insert`, {
        client_id: client_id,
        message: input,
        chat_type: chat_type,
      });

      setInput("");
      fetchMessages();
    } catch (error) {
      console.error("Error fetching messages:", error);
    } finally {
      setIsSending(false); // Reset sending state
    }
  };

  const toggleChat = () => {
    if (isOpen) {
      setClosing(true);
      setTimeout(() => {
        setIsOpen(false);
        setClosing(false);
      }, 300); // Match with CSS transition duration
    } else {
      setIsOpen(true);
    }
  };

  const formatTime = (time) => {
    const date = new Date(time);

    let hours = date.getHours();
    const minutes = date.getMinutes();

    const period = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12;

    const formattedMinutes = minutes < 10 ? "0" + minutes : minutes;

    const formattedTime = `${hours}:${formattedMinutes} ${period}`;
    return formattedTime;
  };

  function isChatTypeExists(data, client_id, chatType) {
    return data.some(item => item.client_id === client_id && item.chat_type.includes(chatType));
  }

  useEffect(() => {
    if (isNotificationRoute) {
      if (notification?.chat)
        setIsOpen(true);
      dispatch(clearNotification());
    }
    if (isOpen)
      fetchMessages();
  }, [isNotificationRoute, isOpen]);

  const chatExists = isChatTypeExists(fcmChatParams, String(client_id), chat_type);
  useEffect(() => {
    if (chatExists)
      fetchMessages();

    if (isOpen)
      dispatch(clearFcmChat({ chat_type, client_id }));
    dispatch(clearNotification());
  }, [isOpen, fcmChatNotificationReceived, newNotificationFound]);

  // Check if send button should be enabled
  const isSendButtonEnabled = input.trim().length > 0 && !isSending;

  return (
    <>
      {isOpen ? (
        <div
          className={`position-fixed bottom-10 end-0 m-3 p-0 bg-white border rounded shadow-lg chatbotmodal ${closing ? "closing" : ""
            }`}
        >
          <div className="chatbox-header d-flex align-items-center justify-content-between p-2">
            <div className="d-flex align-items-center">
              <div className="avatar rounded-circle d-flex align-items-center justify-content-center me-2">
                <img src={chatIcon} alt="robot-icon"></img>
              </div>

              <h5 className="mb-0 text-white">Chat</h5>
            </div>
            <button
              className="btn btn-link text-white p-0 chat-close-button"
              onClick={toggleChat}
            >
              ×
            </button>
          </div>
          <div className="chatbox-body" ref={chatBoxRef}>
            {messages.toReversed().map((message, index) => (
              <div key={index}>
                <div
                  className={`message ${message.team === "2" ? "other" : "user"
                    } d-flex align-items-start mb-2`}
                >
                  <div className="avatar bg-light rounded-circle text-center d-flex align-items-center justify-content-center me-2">
                    <span role="img" aria-label="avatar" className="emoji">
                      🧑‍💻
                    </span>
                  </div>
                  <div>
                    <div className="bg-light p-2 rounded" style={{
                      wordWrap: 'break-word',
                      wordBreak: 'break-word',
                      whiteSpace: 'pre-wrap',
                      maxWidth: '100%',
                      overflowWrap: 'break-word'
                    }}>
                      {message.message}
                    </div>
                    <div className="d-flex align-items-center ">
                      <div className="text-muted chat-size mt-1 pl-2">
                        {message.created_user_profile.name}
                      </div>
                      <div className="text-muted chat-size mt-1 pl-2">
                        {formatTime(message.created_time_formatted)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="input-group p-2 border-top">
            <input
              type="text"
              className="form-control p-1"
              placeholder="Type your message ..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={(e) => e.key === "Enter" && isSendButtonEnabled && handleSend()}
            />
            <div className="input-group-append">
              <button 
                className={`btn ${isSendButtonEnabled ? 'btn-primary' : 'btn-secondary'} chat-send-button`}
                onClick={handleSend}
                disabled={!isSendButtonEnabled}
                style={{
                  opacity: isSendButtonEnabled ? 1 : 0.6,
                  cursor: isSendButtonEnabled ? 'pointer' : 'not-allowed',
                  transition: 'all 0.2s ease-in-out'
                }}
              >
                {isSending ? 'Sending...' : 'Send'}
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div
          className="position-fixed bottom-10 end-0 m-3 p-3 rounded-circle bg-primary d-flex align-items-center justify-content-center messenger-icon"
          onClick={toggleChat}
        >
          {chatExists && <>
            <span className="notification-dot"></span>
          </>}
          <img src={chatbox} alt="robot-icon"></img>
        </div>
      )}
    </>
  );
};

export default ChatPopup;